/**
 * @fileoverview Lazy implementation of Sentry Hub.
 * We lazy instantiate the Sentry hub to defer download the large sentry/browser
 * package.
 * We use the Sentry browser client instead of the default init method because
 * our sentry client should only cover the errors reported from the scope of the
 * widget.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _FwSentryHub_instances, _FwSentryHub_scope, _FwSentryHub_initSentryHub;
import { addInboundFilter } from './eventProcessors/addInboundFilters';
import { addUserAgent } from './eventProcessors/addUserAgent';
let hub;
let sentryHubPromise;
class FwSentryScope {
    constructor() {
        this.tags = [];
    }
    setTag(name, value) {
        this.tags.push([name, value]);
    }
}
export class FwSentryHub {
    constructor(scope) {
        _FwSentryHub_instances.add(this);
        _FwSentryHub_scope.set(this, void 0);
        __classPrivateFieldSet(this, _FwSentryHub_scope, scope, "f");
    }
    configureScope(fn) {
        fn(__classPrivateFieldGet(this, _FwSentryHub_scope, "f"));
    }
    /** Client code should call `captureMessage` from logging.ts */
    captureMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const hub = yield __classPrivateFieldGet(this, _FwSentryHub_instances, "m", _FwSentryHub_initSentryHub).call(this);
            hub.captureMessage(message);
        });
    }
    /** Client code should call `captureException` from logging.ts */
    captureException(error) {
        return __awaiter(this, void 0, void 0, function* () {
            const hub = yield __classPrivateFieldGet(this, _FwSentryHub_instances, "m", _FwSentryHub_initSentryHub).call(this);
            hub.captureException(error);
        });
    }
}
_FwSentryHub_scope = new WeakMap(), _FwSentryHub_instances = new WeakSet(), _FwSentryHub_initSentryHub = function _FwSentryHub_initSentryHub() {
    if (sentryHubPromise) {
        return sentryHubPromise;
    }
    sentryHubPromise = import(
    /* webpackChunkName: 'sentry-browser' */ '@sentry/browser').then(({ BrowserClient, Hub }) => {
        const sentryHub = new Hub(new BrowserClient({
            dsn: 'https://1ef60321bd194094ade30c73d9d39252@sentry.io/2054398',
            environment: process.env.NODE_ENV,
            sampleRate: 0.1,
            release: process.env.RELEASE_VERSION,
            ignoreErrors: [
                // TODO(CS-273): Re-enable this error after we implement a fallback
                // logic when detecting cookie cannot be used.
                `Failed to read the 'cookie' property from 'Document'`,
            ],
            denyUrls: [
                // [CS-242]: ndtv reports `missing target element` because they
                // were using our legacy fwn implementation and didn't put the
                // target element on the page.
                /ndtv\.com/i,
            ],
        }));
        return new Promise((resolve) => {
            sentryHub.run((currentHub) => {
                currentHub.configureScope((scope) => {
                    __classPrivateFieldGet(this, _FwSentryHub_scope, "f").tags.forEach(([name, value]) => scope.setTag(name, value));
                    addUserAgent(scope);
                    addInboundFilter(scope, currentHub);
                });
                resolve(currentHub);
            });
        });
    });
    return sentryHubPromise;
};
/**
 * @returns Sentry hub singleton instance
 */
export const getSentryHub = () => {
    if (!hub) {
        const scope = new FwSentryScope();
        hub = new FwSentryHub(scope);
    }
    return hub;
};
export const TEST_ONLY = {
    reset: () => {
        hub = undefined;
        sentryHubPromise = undefined;
    },
};
